/**
 * ChangePassword — settings flow (authenticated). Mirrors the mobile drawer
 * screen: the API invalidates the session, so a successful change signs the
 * member out and drops them on the public home page.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import accountService from '@wlb/shared/api/services/accountService';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { validatePassword, validatePasswordMatch } from '@wlb/shared/utils/validation';
import type { ApiError } from '@wlb/shared/types';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { MorePage } from '../features/more/MorePage';
import { PasswordCriteria } from '../features/auth/PasswordCriteria';
import '../features/auth/auth.css';
import '../features/settings/settings.css';

export function ChangePassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dirty, setDirty] = useState(false);
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  const newPasswordResult = validatePassword(newPassword);
  const matchResult = validatePasswordMatch(newPassword, confirmPassword);

  const currentPasswordError =
    dirty && !currentPassword.trim() ? t('validation.passwordRequired') : '';
  const newPasswordError =
    dirty && !newPasswordResult.valid ? t(newPasswordResult.message) : '';
  const confirmPasswordError = dirty && !matchResult.valid ? t(matchResult.message) : '';

  const change = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    if (serverError) setServerError('');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setDirty(true);
    if (!currentPassword.trim() || !newPasswordResult.valid || !matchResult.valid) return;

    setSaving(true);
    setServerError('');
    try {
      await accountService.changePassword({ currentPassword, newPassword });
      ToastService.show({ type: 'success', message: t('auth.settingsPassword.changeSuccess') });
      // The API drops the session with the old password, so sign out cleanly.
      navigate('/', { replace: true });
      await logout();
    } catch (caught) {
      const message = getApiErrorMessage(caught as ApiError);
      setServerError(message);
      ToastService.show({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MorePage title={t('auth.settingsPassword.changePasswordTitle')}>
      <form className="settings-form" onSubmit={submit} noValidate>
        <FormError variant="banner" message={serverError} />

        <label className="settings-field">
          <span className="settings-field__label">
            {t('auth.settingsPassword.currentPasswordLabel')}
          </span>
          <PasswordInput
            className={`settings-input${currentPasswordError ? ' settings-input--error' : ''}`}
            value={currentPassword}
            onChange={event => change(setCurrentPassword)(event.target.value)}
            placeholder={t('auth.settingsPassword.currentPasswordPlaceholder')}
            autoComplete="current-password"
            aria-invalid={Boolean(currentPasswordError)}
            aria-describedby={currentPasswordError ? 'current-password-error' : undefined}
          />
          <FormError message={currentPasswordError} id="current-password-error" />
        </label>

        <button
          type="button"
          className="settings-link"
          onClick={() => navigate('/forgot-password')}
        >
          {t('auth.settingsPassword.forgotPasswordLink')}
        </button>

        <label className="settings-field">
          <span className="settings-field__label">
            {t('auth.settingsPassword.newPasswordLabel')}
          </span>
          <PasswordInput
            className={`settings-input${newPasswordError ? ' settings-input--error' : ''}`}
            value={newPassword}
            onChange={event => change(setNewPassword)(event.target.value)}
            placeholder={t('auth.settingsPassword.newPasswordPlaceholder')}
            autoComplete="new-password"
            aria-invalid={Boolean(newPasswordError)}
            aria-describedby={`new-password-criteria${newPasswordError ? ' new-password-error' : ''}`}
          />
          <PasswordCriteria password={newPassword} id="new-password-criteria" />
          <FormError message={newPasswordError} id="new-password-error" />
        </label>

        <label className="settings-field">
          <span className="settings-field__label">
            {t('auth.settingsPassword.confirmPasswordLabel')}
          </span>
          <PasswordInput
            className={`settings-input${confirmPasswordError ? ' settings-input--error' : ''}`}
            value={confirmPassword}
            onChange={event => change(setConfirmPassword)(event.target.value)}
            placeholder={t('auth.settingsPassword.confirmPasswordPlaceholder')}
            autoComplete="new-password"
            aria-invalid={Boolean(confirmPasswordError)}
            aria-describedby={confirmPasswordError ? 'confirm-password-error' : undefined}
          />
          <FormError message={confirmPasswordError} id="confirm-password-error" />
        </label>

        <p className="settings-section__hint">{t('auth.settingsPassword.signOutNotice')}</p>

        <Button type="submit" fullWidth disabled={saving}>
          {t(saving ? 'common.buttons.saving' : 'auth.settingsPassword.savePasswordBtn')}
        </Button>
      </form>
    </MorePage>
  );
}
